import { useState } from "react";
import { Button, Input, message, Modal, Space } from "antd";
import { useAuth } from "../../Contexts/AuthContext";
import { InviteService } from "../../Services/InviteService";
import { TextService } from "../../Services/TextService";
import { eventMailDesign, getEventMailDesignError } from "./EventMailDesign";

type EventMailActionsProps = {
    eventId: number,
    /** True for a reminder, false for an invite. */
    isReminder: boolean,
    getMessage: () => string,
    /** An invite has a subject written by the admin. A reminder has a fixed one. */
    getSubject?: () => string,
    design?: eventMailDesign
}

/**
 * The "preview" and "test send" buttons for an invite or a reminder for an event. Both show the mail
 * the way the recipient gets it: the message, the reply button and the design.
 */
export const EventMailActions = (props: EventMailActionsProps) => {
    const GetText = TextService.GetText;
    const { user } = useAuth();
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isTestSendOpen, setIsTestSendOpen] = useState(false);
    const [testSendLoading, setTestSendLoading] = useState(false);
    const [testEmail, setTestEmail] = useState("");

    const isEmpty = (html: string) => !html || html.length < 1 || html === "<p></p>";

    // The same checks for both buttons: there has to be a message, and the chosen design has to be filled in.
    const isReadyToShow = () => {
        if (isEmpty(props.getMessage())) {
            message.warning(GetText("EmailForm_MessageRequired"));
            return false;
        }

        const designError = getEventMailDesignError(props.design);

        if (designError) {
            message.warning(GetText(designError));
            return false;
        }

        return true;
    };

    const getMail = () => ({
        eventId: props.eventId,
        message: props.getMessage(),
        design: props.design,
        isReminder: props.isReminder
    });

    const onPreview = async () => {
        if (!isReadyToShow()) {
            return;
        }

        setPreviewHtml("");
        setIsPreviewOpen(true);
        setPreviewLoading(true);

        try {
            setPreviewHtml(await InviteService.PreviewMail(getMail()));
        } catch {
            setIsPreviewOpen(false);
            message.error(GetText("EmailForm_PreviewFailed"));
        } finally {
            setPreviewLoading(false);
        }
    };

    const onOpenTestSend = () => {
        if (!isReadyToShow()) {
            return;
        }

        if (!props.isReminder && !props.getSubject?.()) {
            message.warning(GetText("EmailForm_SubjectRequired"));
            return;
        }

        setTestEmail(user?.email ?? "");
        setIsTestSendOpen(true);
    };

    const onTestSend = async () => {
        if (!testEmail.trim()) {
            message.warning(GetText("EventMail_TestSendEmailRequired"));
            return;
        }

        setTestSendLoading(true);

        try {
            await InviteService.SendTestMail({ ...getMail(), subject: props.getSubject?.() ?? "", email: testEmail.trim() });
            message.success(GetText("EventMail_TestSent"));
            setIsTestSendOpen(false);
        } catch {
            message.error(GetText("EmailForm_EmailsSentFailed"));
        } finally {
            setTestSendLoading(false);
        }
    };

    return <>
        {/* Their own Space: in the parent's Space a fragment counts as one item, so the buttons got no gap. */}
        <Space wrap>
            <Button onClick={onPreview} loading={previewLoading}>{GetText("EmailForm_Preview")}</Button>
            <Button onClick={onOpenTestSend} loading={testSendLoading}>{GetText("EmailForm_TestSend")}</Button>
        </Space>

        <Modal
            title={GetText("EmailForm_Preview")}
            width={900}
            open={isPreviewOpen}
            footer={null}
            onCancel={() => { setIsPreviewOpen(false) }}
        >
            <p><i>{GetText("EventMail_PreviewButtonInfo")}</i></p>
            {previewLoading || !previewHtml ?
                <div style={{ width: "100%", height: "560px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #f0f0f0", borderRadius: "6px", backgroundColor: "#ffffff" }}>
                    {GetText("EmailForm_PreviewLoading")}
                </div>
                :
                <iframe
                    title={GetText("EmailForm_Preview")}
                    srcDoc={previewHtml}
                    sandbox=""
                    style={{ width: "100%", height: "560px", border: "1px solid #f0f0f0", borderRadius: "6px", backgroundColor: "#ffffff" }}
                />}
        </Modal>

        <Modal
            title={GetText("EmailForm_TestSendTitle")}
            open={isTestSendOpen}
            okText={GetText("EmailForm_TestSend")}
            cancelText={GetText("Common_Cancel")}
            onOk={onTestSend}
            confirmLoading={testSendLoading}
            onCancel={() => { setIsTestSendOpen(false) }}
        >
            <p><strong>{GetText("EventMail_TestSendTo")}</strong></p>
            <Input type="email" placeholder="example@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
            <p style={{ marginTop: "12px" }}><i>{GetText("EventMail_TestSendInfo")}</i></p>
        </Modal>
    </>;
};
